import { tourTarget, type TourStep } from './types';

export interface AdminTourInput {
  /** A customer to open so the detail steps have a real record to point at. */
  customerId: string | null;
}

/**
 * The staff tour. It covers the four operations Tab Service performs here: set
 * a customer up, manage their keys, move them from practice to live, and answer
 * a question about an e-file they cannot explain.
 */
export function adminTourSteps({ customerId }: AdminTourInput): TourStep[] {
  const admin = { to: '/admin' };
  const steps: TourStep[] = [
    {
      target: tourTarget('admin-customers'),
      title: 'Customers',
      content:
        'Every customer on Tab Service. Mode states whether their e-files reach the IRS test system or the live system. Forms lists what they are permitted to submit. Select a row to open the customer.',
      route: admin,
      placement: 'top',
      disableBeacon: true,
    },
    {
      target: tourTarget('admin-new-customer'),
      title: 'Creating a customer',
      content:
        'Creating a customer performs four actions at once: it invites the owner by email, issues their first practice key, enables the forms selected, and sets whether records are passed to participating states. The key is displayed once.',
      route: admin,
      placement: 'bottom',
    },
  ];

  if (customerId) {
    const detail = { to: '/admin/customers/$customerId', params: { customerId } };
    steps.push(
      {
        target: tourTarget('admin-account'),
        title: 'Practice to live',
        content:
          'This control makes a customer live. It requires the word LIVE to be typed first, because every e-file afterwards is a real IRS submission. Confirm their TCC, enabled forms and test e-files before using it.',
        route: detail,
        placement: 'bottom',
      },
      {
        target: tourTarget('admin-keys'),
        title: 'Keys',
        content:
          'Every key this customer files with, with its mode and last use. Keys are created only by the customer, from their own Settings. If one leaks or a customer must be cut off, revoke it here and it stops working immediately.',
        route: detail,
        placement: 'top',
      },
      {
        target: tourTarget('admin-users'),
        title: 'Users',
        content:
          'The owner is the address invited at creation. They add their own users, so a row that remains invited usually indicates the invitation was not received.',
        route: detail,
        placement: 'top',
      },
      {
        target: tourTarget('admin-customer-usage'),
        title: 'Customer usage',
        content:
          'Records checked, records filed and transmissions per month for this customer alone. These are the figures billing requires.',
        route: detail,
        placement: 'top',
      },
    );
  }

  steps.push(
    {
      target: tourTarget('admin-staff'),
      title: 'Staff accounts',
      content:
        'Everybody at Tab Service who can reach this area. A staff account sees every customer, so it is issued only to the people who support them. Inviting one sends an email to set a password; the first accounts come from the staff bootstrap setting instead.',
      route: { to: '/admin/staff' },
      placement: 'top',
    },
    {
      target: tourTarget('admin-usage-month'),
      title: 'Selecting a month',
      content:
        'Usage is reported one month at a time, going back a year. The selected month is held in the address bar, so a link to a specific month can be pasted into a ticket.',
      route: { to: '/admin/usage' },
      placement: 'bottom',
    },
    {
      target: tourTarget('admin-usage'),
      title: 'Usage across customers',
      content:
        'The same three figures for every customer in the selected month, with a total. Records checked counts everything validated; records filed counts what reached the IRS.',
      route: { to: '/admin/usage' },
      placement: 'top',
    },
    {
      target: tourTarget('nav-e-files'),
      title: 'Supporting a customer',
      content:
        'When a customer asks why an e-file was rejected, open E-files and locate it. Staff see the same screens the customer sees, so the detail under review is identical.',
      route: { to: '/e-files' },
      placement: 'right',
    },
    {
      target: tourTarget('help'),
      title: 'End of tour',
      content:
        'This is your account, at the foot of the menu. Both tours can be restarted from here at any time, and the appearance of the app and the way out are in the same place.',
      route: { to: '/e-files' },
      placement: 'right',
    },
  );

  return steps;
}
